import { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../../api_actions';
import LoadingOrbitalSpinner from '../LoadingOrbitalSpinner/LoadingOrbitalSpinner';

const ModelsCellRenderer = (props) => {
    const models = props.value || [];

    return (
        <div className='models'>
            {Array.isArray(models) && models.map((model, idx) => (
                <div key={idx}>
                    {model}
                </div>
            ))}
        </div>
    );
};

const DiskInfoRenderer = (props) => {
    return (
        <div className='disk-info'>
            <div><strong>Free:</strong> {props.data?.disk_free}</div>
            <div><strong>Total:</strong> {props.data?.disk_total}</div>
            <div><strong>Usage:</strong> {props.data?.disk_usage}</div>
        </div>
    );
};

const BatteryInfoRenderer = (props) => {
    return (
        <div className='battery-info'>
            <div className='battery-info-column'>
                <div><strong>Current:</strong> {props.data?.battery_current}</div>
                <div><strong>Voltage:</strong> {props.data?.battery_voltage}</div>
                <div><strong>Charge State:</strong> {props.data?.battery_charge_state ? 'True' : 'False'}</div>
            </div>
            <div className='battery-info-column'>
                <div><strong>Charger:</strong> {props.data?.battery_charger_on_off ? 'On' : 'Off'}</div>
                <div><strong>PV Voltage:</strong> {props.data?.battery_pv_voltage}</div>
                <div><strong>User Yield (kWh):</strong> {props.data?.battery_user_yield_kwh}</div>
            </div>
        </div>
    );
};

export const SiteDataGridLogic = () => {
    const [deploymentsData, setDeploymentsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const noDataText = 'N/A';

    const overlayComponentSelector = useCallback((params) => {
        if (params.overlayType === "loading") {
            return {
                component: LoadingOrbitalSpinner,
                params: {
                    loadingMessage: "Please wait while data is loading...",
                },
            };
        }
        // return undefined to use the provided overlay for other overlay types
        return undefined;
    }, []);

    const getDeployments = async () => {
        setLoading(true);
        try {
            const res = await apiClient.post(`read/deployments`);
            if (res && res.data) {
                setDeploymentsData(res.data);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getDeployments();
    }, []);

    const transformedData = useMemo(() => {
        return deploymentsData.map(deployment => {
            const depId = deployment.deployment_id;
            const metadata = deployment.periscope_metadata?.[depId] || {};
            const batteryInfo = deployment.battery_info || {};

            return {
                location: deployment.location?.name || noDataText,
                connectivity: deployment.deployment_health?.name || noDataText,
                uptime: metadata.app_uptime ? Math.floor(metadata.app_uptime / 3600) : noDataText,
                fps: metadata.FPS ? Math.floor(metadata.FPS) : noDataText,
                models: metadata.current_models || noDataText,
                // Detail view data
                ram: metadata.RAM || noDataText,
                vram: metadata.VRAM || noDataText,
                power_setting: metadata.power_setting || noDataText,
                disk_free: metadata.disk_free || noDataText,
                disk_total: metadata.disk_total || noDataText,
                disk_usage: metadata.disk_usage || noDataText,
                battery_current: batteryInfo.current || noDataText,
                battery_voltage: batteryInfo.voltage || noDataText,
                battery_charge_state: batteryInfo.charge_state || noDataText,
                battery_charger_on_off: batteryInfo.charger_on_off || noDataText,
                battery_pv_voltage: batteryInfo.pv_voltage || noDataText,
                battery_user_yield_kwh: batteryInfo.user_yield_kwh || noDataText,
                hardware_info_tag: deployment.hardware_info?.tag || noDataText,
                secondary_hardware_info_tag: deployment.secondary_hardware_info?.tag || noDataText,
            };
        });
    }, [deploymentsData]);

    const columnDefs = useMemo(() => [
        {
            headerName: 'Tag',
            field: 'tagDisplay',
            flex: 1,
            valueGetter: (params: any) => {
                const hardware = params.data.hardware_info_tag || '';
                const secondary = params.data.secondary_hardware_info_tag || '';
                return hardware && secondary ? `${hardware} | ${secondary}` : hardware || secondary;
            }
        },
        {
            field: 'connectivity',
            headerName: 'Connectivity',
            flex: 1,
            // minWidth: 120
        },
        {
            field: 'uptime',
            headerName: 'Uptime (h)',
            flex: 1,
            // minWidth: 100
        },
        {
            field: 'fps',
            headerName: 'FPS',
            flex: 1,
            // minWidth: 80
        },
        {
            field: 'models',
            headerName: 'Models',
            flex: 2,
            // minWidth: 200,
            cellRenderer: ModelsCellRenderer
        }
    ], []);

    const detailColumnDefs = useMemo(() => [
        {
            field: 'diskInfo',
            headerName: 'Disk Info',
            flex: 1.5,
            // minWidth: 200,
            cellRenderer: DiskInfoRenderer
        },
        {
            field: 'batteryInfo',
            headerName: 'Battery Info',
            flex: 2,
            // minWidth: 200,
            cellRenderer: BatteryInfoRenderer
        },
        {
            field: 'power_setting',
            headerName: 'Power Setting',
            flex: 1,
            // minWidth: 150
        },
        {
            field: 'ram',
            headerName: 'RAM',
            flex: 1,
            // minWidth: 100
        },
        {
            field: 'vram',
            headerName: 'VRAM',
            flex: 1,
            // minWidth: 100
        }
    ], []);

    const detailCellRendererParams = getDetailCellRendererParams(detailColumnDefs);

    return {
        transformedData,
        columnDefs,
        detailColumnDefs,
        detailCellRendererParams,
        getGridOptions,
        overlayComponentSelector,
        loading,
    };
};

export const getDetailCellRendererParams = (detailColumnDefs) => ({
    detailGridOptions: {
        columnDefs: detailColumnDefs,
        defaultColDef: {
            flex: 1,
        },
        getRowHeight() {
            return 150;
        }
    },
    getDetailRowData: (params) => {
        params.successCallback([params.data]);
    },
});

export const getGridOptions = (columnDefs, transformedData, detailCellRendererParams) => ({
    columnDefs: [
        {
            field: 'location',
            headerName: 'Location',
            // width: 120,
            cellRenderer: 'agGroupCellRenderer',
        },
        ...columnDefs,
    ],
    defaultColDef: {
        flex: 1,
        resizable: true,
    },
    masterDetail: true,
    detailCellRendererParams,
    rowData: transformedData,
    getRowHeight: (params) => {
        if (params.node.detail) {
            return 300;
        }
        return 60;
    },
});
